/**
 * At-a-glance insight for Optimal Posting Times (same shape as Message Heatmap insight).
 * Uses post-weighted engagement (value × sample count) where samples meet the minimum.
 */

import {
  bestPeakHourWindow,
  dayPhraseDaily,
  dayPhraseMonthly,
  dayPhraseWeekly,
  formatHourRangeLabel,
  formatHourShort,
  hourTotalsFromMatrix24,
  shouldShowPeakBand,
} from './messageHeatmapReadability'

/**
 * Weekly 7×24 (day × hour) → 24×7 [hour][sun-first column], weighted by posts per slot.
 * @param {Array<{ dataIndex: number }>} displayDays column order (e.g. Sun→Sat with ISO weekday indices)
 */
export function weeklyWeightedEngagementMatrix24(wMatrix, wSamples, displayDays, minSamples = 1) {
  if (!Array.isArray(displayDays) || displayDays.length === 0) return []
  const matrix24 = Array.from({ length: 24 }, () => Array(displayDays.length).fill(0))
  for (let c = 0; c < displayDays.length; c += 1) {
    const d = displayDays[c].dataIndex
    for (let h = 0; h < 24; h += 1) {
      const val = Number(wMatrix?.[d]?.[h]) || 0
      const samp = Number(wSamples?.[d]?.[h]) || 0
      matrix24[h][c] = samp >= minSamples ? val * samp : 0
    }
  }
  return matrix24
}

/** Trimmed or full hourly grid → weighted 24×N for insight. */
export function gridWeightedEngagementMatrix24(matrix24, samples24, minSamples = 1) {
  if (!Array.isArray(matrix24) || matrix24.length !== 24) return []
  const out = []
  for (let h = 0; h < 24; h += 1) {
    const row = matrix24[h]
    const srow = samples24[h]
    if (!Array.isArray(row)) {
      out.push([])
      continue
    }
    out.push(
      row.map((val, c) => {
        const samp = Number(srow?.[c]) || 0
        return samp >= minSamples ? (Number(val) || 0) * samp : 0
      }),
    )
  }
  return out
}

/**
 * @param {'weekly'|'daily'|'monthly'} viewMode
 * @param {number[][]} matrix24 hour × column (weighted engagement)
 * @param {{ weekDayLabels?: string[], calDates?: string[], monthDates?: string[] }} context
 */
export function buildOptimalPostingInsight(viewMode, matrix24, context) {
  if (!Array.isArray(matrix24) || matrix24.length !== 24) return null
  const grandTotal = matrix24.flat().reduce((s, v) => s + (Number(v) || 0), 0)
  if (grandTotal <= 0) return null

  const hourTotals = hourTotalsFromMatrix24(matrix24)
  const meanHour = grandTotal / 24
  const maxHourTot = Math.max(...hourTotals)
  const hourContrast = meanHour > 0 ? maxHourTot / meanHour : 0

  let dayPart = 'in this view'
  if (viewMode === 'weekly') dayPart = dayPhraseWeekly(matrix24, context?.weekDayLabels)
  else if (viewMode === 'daily') dayPart = dayPhraseDaily(matrix24, context?.calDates)
  else dayPart = dayPhraseMonthly(matrix24, context?.monthDates)

  if (hourContrast < 1.35) {
    return `Engagement is relatively even across hours, with modest variation ${dayPart}.`
  }

  const win = bestPeakHourWindow(hourTotals, 4)
  let timePart = 'across the day'
  if (win && shouldShowPeakBand(hourTotals, win, grandTotal)) {
    timePart = formatHourRangeLabel(win.startH, win.endH)
  } else {
    const hi = hourTotals.indexOf(maxHourTot)
    if (maxHourTot > meanHour * 1.15) timePart = `around ${formatHourShort(hi)}`
  }

  return `Engagement peaks around ${timePart}, strongest ${dayPart}.`
}
